import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Brain, Heart, Shield, Users, Sparkles, BookOpen, Home, Image, FileText } from 'lucide-react'

export default function AboutPage() {
  useEffect(() => {
    // Enable scrolling (override global overflow:hidden)
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [])

  return (
    <div className="min-h-screen bg-ide-bg text-gray-100 overflow-auto" style={{ height: '100vh', overflowY: 'auto' }}>
      <Helmet>
        <title>About P5 AI Studio | Free Vibe Coding & Creative Coding Research</title>
        <meta name="description" content="P5 AI Studio is a free vibe coding platform for p5.js creative coding and generative art. Research project studying dyslexia, prompting patterns, and how people learn to code with AI assistants like GPT-4, Claude, and Gemini." />
        <meta property="og:title" content="About P5 AI Studio | Vibe Coding Research Platform" />
        <meta property="og:description" content="Free creative coding with AI. Research on dyslexia and prompting patterns in p5.js generative art." />
        <meta property="og:url" content="https://p5js.ai/about" />
        <link rel="canonical" href="https://p5js.ai/about" />
      </Helmet>
      
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Header */}
      <header className="border-b border-ide-border bg-ide-sidebar">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-xl font-semibold">About P5 AI Studio</h1>
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <Home size={14} /> IDE
            </Link>
            <Link to="/gallery/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <Image size={14} /> Gallery
            </Link>
            <Link to="/changelog" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <FileText size={14} /> Changelog
            </Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <section className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            Free Vibe Coding for Creative Coders
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            P5 AI Studio is a free <strong>vibe coding</strong> platform for p5.js creative coding 
            and generative art. We provide free access to GPT-4, Claude, and Gemini while 
            conducting research on how people learn to code with AI.
          </p>
        </section>

        {/* Why Free */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Heart className="text-green-400" size={24} />
            </div>
            <h3 className="text-2xl font-semibold">Why Is It Free?</h3>
          </div>
          
          <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border">
            <p className="text-gray-300 leading-relaxed mb-4">
              P5 AI Studio is a <strong>research project</strong> studying how different people 
              interact with AI coding assistants. By using our platform, you're helping us 
              understand patterns in how humans and AI collaborate on creative coding.
            </p>
            <p className="text-gray-300 leading-relaxed">
              In exchange for this valuable research data, we provide <strong>free access</strong> to 
              premium AI models like GPT-4, Claude, and Gemini that would normally cost money to use.
            </p>
          </div>
        </section>

        {/* Research Focus */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Brain className="text-purple-400" size={24} />
            </div>
            <h3 className="text-2xl font-semibold">Our Research Focus</h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border">
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <BookOpen size={18} className="text-blue-400" />
                Dyslexia & Coding
              </h4>
              <p className="text-gray-400 leading-relaxed">
                We're particularly interested in how <strong>dyslexic users</strong> interact 
                with AI assistants compared to non-dyslexic users. This research aims to make 
                coding more accessible by understanding different learning and prompting patterns.
              </p>
            </div>
            
            <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border">
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Sparkles size={18} className="text-yellow-400" />
                Prompting Patterns
              </h4>
              <p className="text-gray-400 leading-relaxed">
                How do people describe what they want to create? What makes a prompt effective? 
                We study these patterns to build better AI tools and help everyone communicate 
                more effectively with AI.
              </p>
            </div>
            
            <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border">
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Users size={18} className="text-green-400" />
                Learning Journeys
              </h4>
              <p className="text-gray-400 leading-relaxed">
                How do beginners progress from simple prompts to complex projects? Understanding 
                these learning journeys helps us design better educational experiences for 
                creative coding.
              </p>
            </div>
            
            <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border">
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Brain size={18} className="text-pink-400" />
                AI Model Comparison
              </h4>
              <p className="text-gray-400 leading-relaxed">
                Which AI models work best for different types of creative coding tasks? Our 
                research compares GPT, Claude, Gemini, and others to understand their strengths 
                and weaknesses.
              </p>
            </div>
          </div>
        </section>

        {/* What We Collect */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Shield className="text-blue-400" size={24} />
            </div>
            <h3 className="text-2xl font-semibold">What We Collect</h3>
          </div>
          
          <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border mb-6">
            <h4 className="font-semibold text-green-400 mb-3">✓ We DO collect:</h4>
            <ul className="space-y-2 text-gray-300">
              <li>• <strong>Your prompts</strong> — What you ask the AI to create</li>
              <li>• <strong>AI responses</strong> — The code the AI generates</li>
              <li>• <strong>Interaction patterns</strong> — How many iterations, time spent, edits made</li>
              <li>• <strong>Optional profile info</strong> — If you choose to share (age range, dyslexia status, coding experience)</li>
              <li>• <strong>Published sketches</strong> — Only what you explicitly choose to share publicly</li>
            </ul>
          </div>
          
          <div className="bg-ide-sidebar rounded-xl p-6 border border-ide-border">
            <h4 className="font-semibold text-red-400 mb-3">✗ We DO NOT collect:</h4>
            <ul className="space-y-2 text-gray-300">
              <li>• Your real name or email (unless you log in with p5.js account)</li>
              <li>• Your location or IP address for tracking</li>
              <li>• Any data you don't generate through the platform</li>
              <li>• Personal files or browser history</li>
            </ul>
          </div>
        </section>

        {/* How to Participate */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-pink-500/20 rounded-lg">
              <Users className="text-pink-400" size={24} />
            </div>
            <h3 className="text-2xl font-semibold">How to Participate</h3>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
            <p className="text-gray-300 leading-relaxed mb-4">
              Simply by using P5 AI Studio, you're already contributing to our research! 
              If you'd like to help even more:
            </p>
            <ol className="space-y-3 text-gray-300">
              <li className="flex gap-3">
                <span className="bg-purple-500/30 text-purple-300 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">1</span>
                <span><strong>Fill out your research profile</strong> — Share optional info about your coding experience and learning style</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-purple-500/30 text-purple-300 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">2</span>
                <span><strong>Use the platform naturally</strong> — Don't change how you prompt, we want authentic data</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-purple-500/30 text-purple-300 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">3</span>
                <span><strong>Publish your creations</strong> — Share your sketches to the gallery to help others learn</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Contact */}
        <section className="text-center">
          <p className="text-gray-400 mb-4">
            Questions about our research or how your data is used?
          </p>
          <a 
            href="mailto:research@p5js.ai" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-ide-accent hover:bg-ide-accentHover text-white rounded-lg transition-colors"
          >
            Contact the Research Team
          </a>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-ide-border mt-16 py-8 text-center text-gray-500 text-sm">
        <p>P5 AI Studio — An xelsed.ai project</p>
        <div className="flex justify-center gap-4 mt-4">
          <Link to="/" className="hover:text-white transition-colors">Editor</Link>
          <Link to="/gallery/" className="hover:text-white transition-colors">Gallery</Link>
          <Link to="/changelog" className="hover:text-white transition-colors">Changelog</Link>
        </div>
      </footer>
    </div>
  )
}

